/**
 * PaceChip — colored chip showing actual vs target pace.
 *
 * Wraps computePace from usePeriodMath. Color thresholds:
 *   green  ≥ 0.95    (Ahead at ≥ 1.05, otherwise On pace)
 *   yellow 0.80-0.94 (Behind)
 *   amber  0.60-0.79 (Behind)
 *   red    < 0.60    (Critical)
 *   neutral target=0 (—)
 */
import { computePace, type PaceColor, type PaceLabel } from "@/lib/usePeriodMath";

const COLORS: Record<PaceColor, { bg: string; fg: string; ring: string }> = {
  green: { bg: "#DCFCE7", fg: "#166534", ring: "#16A34A" },
  yellow: { bg: "#FEF9C3", fg: "#854D0E", ring: "#CA8A04" },
  amber: { bg: "#FFEDD5", fg: "#9A3412", ring: "#EA580C" },
  red: { bg: "#FEE2E2", fg: "#991B1B", ring: "#DC2626" },
  neutral: { bg: "#EFEFEE", fg: "#525258", ring: "#8E8E94" },
};

export interface PaceChipProps {
  actualToDate: number;
  target: number;
  elapsedFraction: number;
  showRatio?: boolean;
  size?: "sm" | "md";
  label?: PaceLabel; // override label
}

export function PaceChip({ actualToDate, target, elapsedFraction, showRatio = true, size = "sm", label }: PaceChipProps) {
  const r = computePace(actualToDate, target, elapsedFraction);
  const c = COLORS[r.color];
  const text = label ?? r.label;
  const pad = size === "md" ? "px-2.5 py-1 text-[12px]" : "px-2 py-0.5 text-[11px]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${pad}`}
      style={{ background: c.bg, color: c.fg, borderColor: c.ring, borderWidth: 1, borderStyle: "solid" }}
      data-testid={`pace-chip-${r.color}`}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: c.ring }} />
      <span>{text}</span>
      {showRatio && r.ratio > 0 && (
        <span className="opacity-70">{Math.round(r.ratio * 100)}%</span>
      )}
    </span>
  );
}

export default PaceChip;
